import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { AppHeaderProvider, isCaseHeaderPath } from "@/components/app-header";
import { AppSidebar } from "@/components/app-sidebar";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { AgentAttentionLayer } from "@/features/agentAttention/components/AgentAttentionLayer";
import { AppTabBar } from "@/features/appTabs/components/AppTabBar";
import { ActiveTabProvider } from "@/features/appTabs/context/ActiveTabContext";
import { isCalendarPath } from "@/features/calendar/routes";
import { NEW_CASE_PATH } from "@/features/caseIde/routes";
import { COMPANIES_PATH } from "@/features/companies/routes";
import { CONNECTORS_PATH } from "@/features/connectors/routes";
import { isHomePath } from "@/features/dashboard/routes";
import { DESIGN_SYSTEM_PATH } from "@/features/designSystem/routes";
import { AppChecklist } from "@/features/onboarding/components/AppChecklist";
import { isOnboardingPath } from "@/features/onboarding/routes";
import { TIME_PATH } from "@/features/timeTracking/routes";
import { NewTutorialPopup } from "@/features/tutorials";
import { TUTORIALS_PATH } from "@/features/tutorials/routes";
import {
  GlobalSearchDialog,
  GlobalSearchProvider
} from "@/features/globalSearch";
import { SettingsPage } from "@/components/templates";
import { TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { AnnouncementStrip } from "../components/layout/AnnouncementStrip";
import { ImpersonationBanner } from "../components/layout/ImpersonationBanner";
import { ShellEffects } from "../components/layout/ShellEffects";

interface PageWrapperProps {
  component: React.JSX.Element;
}

const PageWrapper = ({ component }: PageWrapperProps) => {
  const location = useLocation();
  const mainRef = useRef<HTMLDivElement | null>(null);
  const isAthena = location.pathname.startsWith("/athena");
  const isClients = location.pathname.startsWith("/clients");
  const isCases =
    location.pathname === "/cases" || location.pathname === NEW_CASE_PATH;
  const isAdminLawFirms = location.pathname.startsWith("/admin/law-firms");
  const isAdminUpdates = location.pathname.startsWith("/admin/updates");
  const isCompanies =
    location.pathname === COMPANIES_PATH ||
    location.pathname.startsWith(`${COMPANIES_PATH}/`);
  const isTimesheet =
    location.pathname === TIME_PATH ||
    location.pathname.startsWith(`${TIME_PATH}/`);
  const isFirmKnowledge = location.pathname.startsWith("/firm-knowledge");
  const isAutomations = location.pathname.startsWith("/automations");
  const isConnectors = location.pathname.startsWith(CONNECTORS_PATH);
  const isTutorials = location.pathname.startsWith(TUTORIALS_PATH);
  const isLawFirmSso =
    location.pathname.startsWith("/law-firm/sso-setup") ||
    location.pathname.startsWith("/law-firm/sso-management");
  const isCaseRoute = isCaseHeaderPath(location.pathname);
  // Case routes and the individual account pages use the full-bleed layout.
  const isIndividual =
    isCaseRoute || location.pathname.startsWith("/individual/");
  // Home has its own scrolling column and activity rail.
  const isHome = isHomePath(location.pathname);
  const isCalendar = isCalendarPath(location.pathname);
  const isDesignSystem = location.pathname.startsWith(DESIGN_SYSTEM_PATH);
  // The guided first run shows only its Get Started tab, and the sidebar asks
  // before a link skips setup.
  const isOnboarding = isOnboardingPath(location.pathname);
  const isFullBleedLayout =
    isOnboarding ||
    isAthena ||
    isClients ||
    isHome ||
    isCases ||
    isCompanies ||
    isTimesheet ||
    isIndividual ||
    isLawFirmSso ||
    isAdminLawFirms ||
    isAdminUpdates ||
    isFirmKnowledge ||
    isAutomations ||
    isConnectors ||
    isTutorials ||
    isCalendar ||
    isDesignSystem;

  useEffect(() => {
    mainRef?.current?.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <ActiveTabProvider>
      <ShellEffects />
      <TooltipProvider delayDuration={0}>
        <div className="flex h-full min-h-0 flex-col">
          <AnnouncementStrip />
          {/*
            The sidebar starts as the icon rail on every load and sign-in, and
            opens only when someone clicks the rail (or presses Cmd/Ctrl+B).
            It keeps that choice while they move around, not between visits.
          */}
          <SidebarProvider
            defaultOpen={false}
            className="min-h-0 flex-1 overflow-hidden"
          >
            <AppHeaderProvider>
              <GlobalSearchProvider>
                <AppSidebar guardNavigation={isOnboarding} />
                <SidebarInset className="min-h-0 min-w-0 overflow-hidden">
                  <ImpersonationBanner />
                  <AppTabBar focused={isOnboarding} />
                  <GlobalSearchDialog />
                  <AgentAttentionLayer />
                  <div className="flex min-h-0 flex-1 overflow-hidden bg-surface">
                    <main
                      id="scrollable-div"
                      ref={mainRef}
                      className={cn(
                        "flex min-h-0 w-full flex-1 flex-col",
                        isIndividual && "overscroll-none",
                        isFullBleedLayout
                          ? "overflow-hidden"
                          : "overflow-x-hidden overflow-y-auto"
                      )}
                    >
                      {isFullBleedLayout ? (
                        component
                      ) : (
                        <SettingsPage width="wide" scroll={false}>
                          {component}
                        </SettingsPage>
                      )}
                    </main>
                  </div>
                  <AppChecklist />
                  <NewTutorialPopup />
                </SidebarInset>
              </GlobalSearchProvider>
            </AppHeaderProvider>
          </SidebarProvider>
        </div>
      </TooltipProvider>
    </ActiveTabProvider>
  );
};

export default PageWrapper;
